import React, { useEffect, useState } from "react";
import { LiveStrategiesService } from "./services/strategyService";
import Navbar from "./navbar";
import Footer from "./footer";
import { useNavigate } from "react-router-dom";

const LiveStrategiesTable = () => {
  const [strategies, setStrategies] = useState([]);
  const [filteredStrategies, setFilteredStrategies] = useState([]); // State for filtered strategies
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(""); // State for search term
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [sortField, setSortField] = useState("xirr"); // Default sort field
  const [sortOrder, setSortOrder] = useState("asc"); // Default sort order (ascending)
  const navigate = useNavigate();
  const [showRunModal, setShowRunModal] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [runType, setRunType] = useState("full");
  const [runDate, setRunDate] = useState("");
  const [running, setRunning] = useState(false);
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  // Fetch data and set the strategies
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await LiveStrategiesService();
        setStrategies(data);
        setFilteredStrategies(data); // Initially, all strategies are displayed
      } catch (error) {
        setError("Failed to fetch live strategies");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter strategies based on search term
  useEffect(() => {
    if (searchTerm === "") {
      setFilteredStrategies(strategies);
    } else {
      const filtered = strategies.filter((strategy) =>
        strategy.strategyCode.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredStrategies(filtered);
    }
  }, [searchTerm, strategies]);

  // Sorting function
  const handleSort = (field) => {
    const newSortOrder =
      sortField === field && sortOrder === "asc" ? "desc" : "asc"; // Toggle sort order
    setSortField(field);
    setSortOrder(newSortOrder);

    // Sort data based on the selected field and order
    const sortedData = [...filteredStrategies].sort((a, b) => {
      if (field === "createdOn" || field === "modifiedOn") {
        // Convert to Date if the field is createdOn or modifiedOn
        const dateA = new Date(a[field]);
        const dateB = new Date(b[field]);
        return newSortOrder === "asc" ? dateA - dateB : dateB - dateA;
      } else {
        // Handle numerical fields like XIRR
        return newSortOrder === "asc"
          ? a[field] - b[field]
          : b[field] - a[field];
      }
    });
    setFilteredStrategies(sortedData);
    // Save the sort preferences in localStorage
    localStorage.setItem("sortField", field);
    localStorage.setItem("sortOrder", newSortOrder);
  };

  // Load the sort preferences from localStorage on component mount
  useEffect(() => {
    const savedSortField = localStorage.getItem("sortField");
    const savedSortOrder = localStorage.getItem("sortOrder");
    if (savedSortField && savedSortOrder) {
      setSortField(savedSortField);
      setSortOrder(savedSortOrder);
    }
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }
  if (error) {
    return <div>{error}</div>;
  }

  const handleStrategyClick = (strategyCode) => {
    navigate(`/strategyDataManager/${strategyCode}`);
  };

  const handlePreview = (strategyCode) => {
    navigate(`/strategyDataManager/${strategyCode}`, {
      state: { fromPreview: true },
    });
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentStrategies = filteredStrategies.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(filteredStrategies.length / itemsPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxButtons = 5; // Number of pagination buttons to show
    const startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    const endPage = Math.min(totalPages, startPage + maxButtons - 1);

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <li
          className={`page-item ${currentPage === i ? "active" : ""}`}
          key={i}
        >
          <button className="page-link" onClick={() => paginate(i)}>
            {i}
          </button>
        </li>,
      );
    }

    if (startPage > 1) {
      buttons.unshift(
        <li className="page-item" key="start-ellipsis">
          <span className="page-link">...</span>
        </li>,
      );
    }
    if (endPage < totalPages) {
      buttons.push(
        <li className="page-item" key="end-ellipsis">
          <span className="page-link">...</span>
        </li>,
      );
    }
    return buttons;
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1); // Reset to the first page on size change
  };

  const runStrategy = async () => {
    if (!selectedStrategy) return;

    if (runType === "incremental" && !runDate) {
      alert("Please select run date");
      return;
    }

    setRunning(true);

    try {
      let url = "";

      if (runType === "full") {
        url = `${BASE_URL}/backtest/save/strategy/${selectedStrategy}`;
      } else {
        url = `${BASE_URL}/backtest/incremental/strategy/${selectedStrategy}/${runDate}`;
      }

      const response = await fetch(url, {
        method: "GET",
      });

      if (!response.ok) {
        throw new Error("Run failed");
      }

      alert("Strategy run started successfully");
      setShowRunModal(false);
    } catch (e) {
      alert("Error while running strategy");
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="main-content">
        <div className="container-fluid">
          {/* Left-aligned Back Button */}
          <div
            className="d-flex justify-content-between"
            style={{ marginBottom: "10px", marginTop: "10px" }}
          >
            <button
              className="btn btn-primary d-flex justify-content-start"
              onClick={() => navigate(-1)}
            >
              Back
            </button>
            <h4 className="fw-bold">Live Strategies</h4>
            <h4></h4>
          </div>

          <div className="mb-3">
            <div className="row">
              <div className="col-md-3" style={{ width: "15%" }}>
                {" "}
                {/* Adjust the column size as needed */}
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by Strategy Code"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="mb-9">
            <table className="table table-striped table-borderless">
              <thead>
                <tr>
                  <th>Strategy Code</th>
                  <th onClick={() => handleSort("xirr")}>
                    XIRR (%){" "}
                    {sortField === "xirr" && (sortOrder === "asc" ? "▲" : "▼")}
                  </th>
                  <th>Net Return</th>
                  <th>Drawdown Start Date</th>
                  <th>Drawdown End Date</th>
                  <th>Drawdown Percentage (%)</th>
                  <th>Status</th>
                  <th onClick={() => handleSort("createdOn")}>
                    Created On{" "}
                    {sortField === "createdOn" &&
                      (sortOrder === "asc" ? "▲" : "▼")}
                  </th>
                  <th onClick={() => handleSort("modifiedOn")}>
                    Modified On{" "}
                    {sortField === "modifiedOn" &&
                      (sortOrder === "asc" ? "▲" : "▼")}
                  </th>
                  <th>Run</th>
                  <th>Preview</th>
                  <th>Replay</th>
                </tr>
              </thead>
              <tbody>
                {currentStrategies.map((strategy) => (
                  <tr key={strategy.id}>
                    <td
                      style={{ cursor: "pointer", color: "blue" }}
                      onClick={() => handleStrategyClick(strategy.strategyCode)} // Trigger redirect on click
                    >
                      {strategy.strategyCode}
                    </td>
                    <td>{strategy.xirr}</td>
                    <td>{strategy.netReturn}</td>
                    <td>{strategy.drawDownStartDate}</td>
                    <td>{strategy.drawDownEndDate}</td>
                    <td>{strategy.drawDownPercentage}</td>
                    <td>{strategy.status === 1 ? "Active" : "Inactive"}</td>
                    <td>{new Date(strategy.createdOn).toLocaleString()}</td>
                    <td>{new Date(strategy.modifiedOn).toLocaleString()}</td>
                    <td>
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          setSelectedStrategy(strategy.strategyCode);
                          setRunType("full");
                          setRunDate("");
                          setShowRunModal(true);
                        }}
                      >
                        Run
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handlePreview(strategy.strategyCode)}
                      >
                        Preview
                      </button>
                    </td>
                    <td>
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`${BASE_URL}/backTestStrategy/${strategy.strategyCode}`}
                      >
                        Replay
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <nav className="d-flex justify-content-center mt-3">
              <ul className="pagination">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                </li>
                {renderPaginationButtons()}
                <li
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <ul>
                  Show{" "}
                  <select
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    style={{ width: "60px", height: "30px", fontSize: "14px" }}
                  >
                    <option value={15}>15</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  entries
                </ul>
              </div>
            </nav>
          )}
        </div>
      </div>
      <Footer />

      {showRunModal && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Run Strategy</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowRunModal(false)}
                />
              </div>

              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Run Type</label>
                  <select
                    className="form-select"
                    value={runType}
                    onChange={(e) => setRunType(e.target.value)}
                  >
                    <option value="full">Full Run</option>
                    <option value="incremental">Incremental Run</option>
                  </select>
                </div>

                {runType === "incremental" && (
                  <div className="mb-3">
                    <label className="form-label">Run Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={runDate}
                      onChange={(e) => setRunDate(e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowRunModal(false)}
                  disabled={running}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={runStrategy}
                  disabled={running}
                >
                  {running ? "Running..." : "Run"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LiveStrategiesTable;
